import React, { useState, useEffect } from "react";
import { Modal, Box, Button, CircularProgress } from "@mui/material";
import { BASE_URL } from "../../../../components/BaseUrl"; 
import { toast } from "react-hot-toast"; 
import { decryptToken } from "../../../../Utlis/Encryption";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import logoutmodel from "../../../../assets/logoutmodel.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";

const RatingModal = ({ open, onClose, taskId, onRatingSubmit, existingRating = null, existingNotes = null }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  // Pre-fill fields when existing rating is provided or modal opens
  useEffect(() => {
    if (open) {
      setRating(existingRating || "");
      setNotes(existingNotes || "");
    }
  }, [open, existingRating, existingNotes]);

  const handleRatingChange = (value) => {
    setRating(value);
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  const handleSubmit = async () => {
    if (!rating) {
      toast.error(t('ratingModal.ratingError'));
      return;
    }

    setLoading(true);
    try {
      // Use PUT if editing (existing rating), POST if adding new
      const isEdit = !!existingRating;
      const method = "POST";
      
      const response = await fetch(`${BASE_URL}/task-management/admin/tasks/${taskId}/productivity`, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${decryptedToken}`, // Pass the token in the Authorization header
        },
        body: JSON.stringify({
          rating: rating,
          notes: notes,
        }),
      });

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.message || "API call failed");
      }

      toast.success(isEdit ? t('ratingModal.ratingUpdated') || "Rating updated successfully" : t('ratingModal.ratingSuccess'));
      onRatingSubmit(); // callback to update the task details (status)
      onClose(); // Close the modal after submission
    } catch (err) {
      toast.error(t('ratingModal.errorAddingRating', { errorMessage: err.message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <img
            src={logoutmodel}
            alt="logout"
            style={{ width: 50, height: 50 }}
          />
        </Box>
        <CustomText sx={{ ...textStyles.h1, fontSize: 18, fontWeight: 500, lineHeight: "32px", color: "#2C384C" }}>
          {existingRating ? (t('ratingModal.editTitle') || "Edit Rating") : t('ratingModal.title')}
        </CustomText>
        <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 2 }}>
          <Button
            variant={rating === "good" ? "contained" : "outlined"}
            onClick={() => handleRatingChange("good")}
            sx={{
              flex: 1,
              fontFamily: 'Poppins, sans-serif',
              textTransform: "capitalize"
            }}
          >
            {t('ratingModal.good')}
          </Button>
          <Button
            variant={rating === "bad" ? "contained" : "outlined"}
            onClick={() => handleRatingChange("bad")}
            sx={{
              flex: 1,
              fontFamily: 'Poppins, sans-serif',
              textTransform: "capitalize"
            }}
          >
            {t('ratingModal.bad')}
          </Button>
        </Box>

        <textarea
          className="project_input"
          placeholder={t('ratingModal.notesLabel')}
          value={notes}
          onChange={handleNotesChange}
          style={{
            width: "100%",
            height: "90px",
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <CustomButton
            onClick={onClose}
            size="medium"
            label={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{t('ratingModal.closeButton')}</Box>}
            sx={{
              color: "#172B4D",
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: "white",
                color: "#172B4D",
              },
            }}
          />

          <CustomButton
            onClick={handleSubmit}
            disabled={loading}
            size="medium"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {loading && (
                  <CircularProgress size={18} thickness={4} sx={{ color: "#ffffff" }} />
                )}
                {existingRating ? (t('ratingModal.updateRatingButton') || "Update Rating") : t('ratingModal.addRatingButton')}
              </Box>
            }
          />
        </Box>
      </Box>
    </Modal>
  );
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  padding: 3,
  boxShadow: 24,
  borderRadius: "8px",
  width: 400,
};

export default RatingModal;
